import { CSSProperties, MutableRefObject, useState } from 'react'
import { createPortal } from 'react-dom'
import { MagicWandIcon } from 'assets/icons/magic-wand'
import {
  GREEN_AUTO_MAP_ADVANCED_TOGGLES,
  GREEN_AUTO_MAP_LOOSENESS_SLIDER,
  GREEN_AUTO_MAP_PANEL_SLIDERS,
  GREEN_AUTO_MAP_POINTS_SLIDER,
  GreenAutoMapNumericParamKey,
  GreenAutoMapParamKey,
  GreenAutoMapSliderConfig,
  GreenAutoMapToggleConfig,
} from 'components/gps-editor/greenAutoMapControlConfig'
import {
  GreenAutoMapControl,
  GreenAutoMapControlHeader,
  GreenAutoMapControlRange,
  GreenAutoMapControlValue,
  GreenAutoMapPanel,
  GreenAutoMapPanelCloseButton,
  GreenAutoMapPanelHeader,
  GreenAutoMapPanelHeaderActions,
  GreenAutoMapPanelTitle,
  GreenAutoMapPointsValue,
  GreenAutoMapPrimaryButton,
  GreenAutoMapResetButton,
  GreenAutoMapSecondaryButton,
  GreenAutoMapSlider,
  GreenAutoMapToggleDescription,
  GreenAutoMapToggleGroup,
  GreenAutoMapToggleInput,
  GreenAutoMapToggleLabel,
  GreenAutoMapToggleRow,
  GreenAutoMapToggleText,
  GreenAutoMapToolbar,
  GreenAutoMapToolbarActions,
  GreenAutoMapToolbarSlider,
  GreenAutoMapToolbarSliderInput,
  GreenAutoMapToolbarSliderLabel,
  GreenAutoMapToolbarStatus,
  GreenAutoMapToolbarStepper,
  GreenAutoMapToolbarStepperButton,
  GreenAutoMapToolbarStepperValue,
  GreenAutoMapToolbarToggle,
  GreenAutoMapTuningActions,
  GreenAutoMapTuningDescription,
  GreenAutoMapTuningSection,
  GreenAutoMapTuningStatus,
  GreenAutoMapTuningTitle,
  GreenToolActions,
  GreenToolSecondaryButton,
  GreenToolStack,
  IconDivider,
  SliderBackground,
} from 'components/gps-editor/CourseGpsEditorElements.styles'
import { GreenAutoMapParams } from 'utils/gpsEditor/gpsUtils'
import { getActiveGreenAutoMapTuning } from 'utils/gpsEditor/parameterSelection'
import {
  clearPersistedGreenAutoMapTuning,
  parseGreenAutoMapTuning,
  persistGreenAutoMapTuning,
} from 'scenes/gps-editor/courseGpsEditor.utils'
import { AdjustmentsIcon } from 'assets/icons/adjustments'
import { RemapIcon } from 'assets/icons/remap'
import { CheckCircleIcon } from 'assets/icons/check-circle'
import { ClearIcon } from 'assets/icons/clear'
import { HazardToolIcon } from 'assets/icons/hazard-tool'

interface Props {
  activeGreenAutoMapSliderKeyRef: MutableRefObject<GreenAutoMapNumericParamKey | null>
  activeHolePolygonPointsCount: number
  commitActiveGreenAutoMapSlider: () => void
  commitGreenAutoMapParam: (
    key: GreenAutoMapParamKey,
    value: GreenAutoMapParams[GreenAutoMapParamKey],
    shouldRemap?: boolean,
  ) => void
  formatGreenAutoMapValue: (config: GreenAutoMapSliderConfig, value: number) => string
  greenAutoMapAdvancedOpen: boolean
  greenAutoMapBusy: boolean
  greenAutoMapControlsVisible: boolean
  greenAutoMapDefaultsActive: boolean
  greenAutoMapDraftParams: GreenAutoMapParams
  greenAutoMapPreviewReady: boolean
  guidedGreenAutoMapActive: boolean
  guidedGreenAutoMapProgressLabel: string | null
  greenMapping: boolean
  greenPolygonFinished: boolean
  onAcceptGreenAutoMapPreview: () => void
  onConfirmManualGuidedGreen: () => void
  onAutoMapGreen: (params?: GreenAutoMapParams) => void
  onClearGreenPolygon: () => void
  onCloseGreenAutoMapControls: () => void
  onFinishGreenPolygon: () => void
  onResetGreenAutoMapParams: () => void
  setGreenAutoMapAdvancedOpen: (value: boolean | ((prev: boolean) => boolean)) => void
  updateGreenAutoMapDraftParam: (key: GreenAutoMapParamKey, value: GreenAutoMapParams[GreenAutoMapParamKey]) => void
  showAdvancedPanel?: boolean
}

export default function CourseGpsEditorGreenAutoMapControls({
  activeGreenAutoMapSliderKeyRef,
  activeHolePolygonPointsCount,
  commitActiveGreenAutoMapSlider,
  commitGreenAutoMapParam,
  formatGreenAutoMapValue,
  greenAutoMapAdvancedOpen,
  greenAutoMapBusy,
  greenAutoMapControlsVisible,
  greenAutoMapDefaultsActive,
  greenAutoMapDraftParams,
  greenAutoMapPreviewReady,
  guidedGreenAutoMapActive,
  guidedGreenAutoMapProgressLabel,
  greenMapping,
  greenPolygonFinished,
  onAcceptGreenAutoMapPreview,
  onConfirmManualGuidedGreen,
  onAutoMapGreen,
  onClearGreenPolygon,
  onCloseGreenAutoMapControls,
  onFinishGreenPolygon,
  onResetGreenAutoMapParams,
  setGreenAutoMapAdvancedOpen,
  updateGreenAutoMapDraftParam,
  showAdvancedPanel,
}: Props): JSX.Element | null {
  const getSliderStyle = (config: GreenAutoMapSliderConfig, value: number): CSSProperties => {
    const progress = ((value - config.min) / (config.max - config.min)) * 100

    return {
      ['--slider-progress' as string]: `${Math.min(100, Math.max(0, progress))}%`,
    }
  }

  const handleGreenAutoMapRemap = () => {
    onAutoMapGreen(greenAutoMapDraftParams)
  }

  const [tuningStatus, setTuningStatus] = useState<{ tone: 'info' | 'error'; message: string } | null>(null)

  const handleExportTuning = async () => {
    const json = JSON.stringify(getActiveGreenAutoMapTuning(), null, 2)
    try {
      await navigator.clipboard.writeText(json)
      setTuningStatus({ tone: 'info', message: 'Copied tuning JSON to clipboard.' })
    } catch (error) {
      console.error('Failed to copy tuning JSON', error)
      console.log('Tuning JSON:\n' + json)
      setTuningStatus({ tone: 'error', message: 'Clipboard unavailable. JSON logged to console.' })
    }
  }

  const handleImportTuning = async () => {
    try {
      const text = await navigator.clipboard.readText()
      if (!text) {
        setTuningStatus({ tone: 'error', message: 'Clipboard is empty.' })
        return
      }
      const tuning = parseGreenAutoMapTuning(JSON.parse(text))
      persistGreenAutoMapTuning(tuning)
      setTuningStatus({ tone: 'info', message: 'Tuning applied. Remapping...' })
      onAutoMapGreen(greenAutoMapDraftParams)
    } catch (error) {
      console.error('Failed to import tuning JSON', error)
      const message = error instanceof Error ? error.message : 'Invalid JSON.'
      setTuningStatus({ tone: 'error', message: `Import failed: ${message}` })
    }
  }

  const handleResetTuning = () => {
    clearPersistedGreenAutoMapTuning()
    setTuningStatus({ tone: 'info', message: 'Tuning reset to defaults. Remapping...' })
    onAutoMapGreen(greenAutoMapDraftParams)
  }

  const handleCloseGreenAutoMapSession = () => {
    setGreenAutoMapAdvancedOpen(false)
    onAcceptGreenAutoMapPreview()

    if (!guidedGreenAutoMapActive) {
      onCloseGreenAutoMapControls()
    }
  }

  const renderGreenAutoMapSlider = (config: GreenAutoMapSliderConfig) => {
    const value = greenAutoMapDraftParams[config.key] as number
    const isLoosenessSlider = config.key === 'loosenessBias'
    const isLoosenessDisabled =
      isLoosenessSlider && (greenAutoMapDraftParams.lPresetIndex >= 0 || greenAutoMapDraftParams.abPresetIndex >= 0)

    return (
      <GreenAutoMapControl key={config.key}>
        <GreenAutoMapControlHeader>
          <span>{config.label}</span>
          <GreenAutoMapControlValue>{formatGreenAutoMapValue(config, value)}</GreenAutoMapControlValue>
        </GreenAutoMapControlHeader>
        <GreenAutoMapSlider
          type="range"
          min={config.min}
          max={config.max}
          step={config.step}
          value={value}
          style={getSliderStyle(config, value)}
          disabled={greenAutoMapBusy || isLoosenessDisabled}
          onPointerDown={() => {
            activeGreenAutoMapSliderKeyRef.current = config.key
          }}
          onChange={(event) => updateGreenAutoMapDraftParam(config.key, Number(event.target.value))}
          onKeyUp={(event) => {
            activeGreenAutoMapSliderKeyRef.current = null
            commitGreenAutoMapParam(config.key, Number(event.currentTarget.value), true)
          }}
          onBlur={() => commitActiveGreenAutoMapSlider()}
        />
        <GreenAutoMapControlRange>
          <span>{formatGreenAutoMapValue(config, config.min)}</span>
          <span>{formatGreenAutoMapValue(config, config.max)}</span>
        </GreenAutoMapControlRange>
      </GreenAutoMapControl>
    )
  }

  const renderGreenAutoMapToggle = (config: GreenAutoMapToggleConfig) => {
    const checked = greenAutoMapDraftParams[config.key] as boolean

    return (
      <GreenAutoMapToggleRow key={config.key}>
        <GreenAutoMapToggleText>
          <GreenAutoMapToggleLabel>{config.label}</GreenAutoMapToggleLabel>
          <GreenAutoMapToggleDescription>{config.description}</GreenAutoMapToggleDescription>
        </GreenAutoMapToggleText>
        <GreenAutoMapToggleInput
          type="checkbox"
          checked={checked}
          disabled={greenAutoMapBusy}
          onChange={(event) => {
            const nextChecked = event.currentTarget.checked
            updateGreenAutoMapDraftParam(config.key, nextChecked)
            commitGreenAutoMapParam(config.key, nextChecked, config.shouldRemap)
          }}
        />
      </GreenAutoMapToggleRow>
    )
  }

  if (!greenMapping) {
    return null
  }

  const showAutoMapAction = !greenAutoMapControlsVisible
  const showFinishAction = activeHolePolygonPointsCount > 2 && !greenPolygonFinished
  const showClearAction = activeHolePolygonPointsCount > 0 && greenPolygonFinished && !greenAutoMapControlsVisible
  const showGreenToolActions = showAutoMapAction || showFinishAction || showClearAction
  const showGuidedManualToolbar = guidedGreenAutoMapActive && !greenAutoMapControlsVisible

  const advancedPanel =
    showAdvancedPanel &&
    greenAutoMapControlsVisible &&
    greenAutoMapAdvancedOpen &&
    typeof document !== 'undefined' &&
    createPortal(
      <GreenAutoMapPanel>
        <GreenAutoMapPanelHeader>
          <GreenAutoMapPanelTitle>Green auto-map parameters</GreenAutoMapPanelTitle>
          <GreenAutoMapPanelHeaderActions>
            <GreenAutoMapResetButton
              type="button"
              disabled={greenAutoMapBusy || greenAutoMapDefaultsActive}
              onClick={onResetGreenAutoMapParams}
            >
              Reset
            </GreenAutoMapResetButton>
            <GreenAutoMapPanelCloseButton
              type="button"
              aria-label="Close green auto-map parameters"
              onClick={() => setGreenAutoMapAdvancedOpen(false)}
            >
              ×
            </GreenAutoMapPanelCloseButton>
          </GreenAutoMapPanelHeaderActions>
        </GreenAutoMapPanelHeader>

        {GREEN_AUTO_MAP_PANEL_SLIDERS.map(renderGreenAutoMapSlider)}
        <GreenAutoMapToggleGroup>
          {GREEN_AUTO_MAP_ADVANCED_TOGGLES.map(renderGreenAutoMapToggle)}
        </GreenAutoMapToggleGroup>
        <GreenAutoMapTuningSection>
          <GreenAutoMapTuningTitle>Algorithm tuning JSON</GreenAutoMapTuningTitle>
          <GreenAutoMapTuningDescription>
            Persist L params, AB params and probe thresholds to local storage. Copy the current values, paste an edited
            JSON, or reset back to defaults.
          </GreenAutoMapTuningDescription>
          <GreenAutoMapTuningActions>
            <GreenAutoMapSecondaryButton type="button" onClick={handleExportTuning}>
              Copy JSON
            </GreenAutoMapSecondaryButton>
            <GreenAutoMapSecondaryButton type="button" onClick={handleImportTuning} disabled={greenAutoMapBusy}>
              Paste & apply
            </GreenAutoMapSecondaryButton>
            <GreenAutoMapSecondaryButton type="button" onClick={handleResetTuning} disabled={greenAutoMapBusy}>
              Reset to defaults
            </GreenAutoMapSecondaryButton>
          </GreenAutoMapTuningActions>
          {tuningStatus && (
            <GreenAutoMapTuningStatus $tone={tuningStatus.tone}>{tuningStatus.message}</GreenAutoMapTuningStatus>
          )}
        </GreenAutoMapTuningSection>
      </GreenAutoMapPanel>,
      document.body,
    )

  return (
    <>
      {advancedPanel}

      <GreenToolStack>
        {greenAutoMapControlsVisible && (
          <GreenAutoMapToolbar>
            <GreenAutoMapToolbarSlider>
              <GreenAutoMapToolbarSliderLabel>Points</GreenAutoMapToolbarSliderLabel>
              <SliderBackground>
                <GreenAutoMapToolbarSliderInput
                  type="range"
                  min={GREEN_AUTO_MAP_POINTS_SLIDER.min}
                  max={GREEN_AUTO_MAP_POINTS_SLIDER.max}
                  step={GREEN_AUTO_MAP_POINTS_SLIDER.step}
                  value={greenAutoMapDraftParams[GREEN_AUTO_MAP_POINTS_SLIDER.key]}
                  style={getSliderStyle(
                    GREEN_AUTO_MAP_POINTS_SLIDER,
                    greenAutoMapDraftParams[GREEN_AUTO_MAP_POINTS_SLIDER.key],
                  )}
                  disabled={greenAutoMapBusy}
                  onPointerDown={() => {
                    activeGreenAutoMapSliderKeyRef.current = GREEN_AUTO_MAP_POINTS_SLIDER.key
                  }}
                  onChange={(event) =>
                    updateGreenAutoMapDraftParam(GREEN_AUTO_MAP_POINTS_SLIDER.key, Number(event.target.value))
                  }
                  onKeyUp={(event) => {
                    activeGreenAutoMapSliderKeyRef.current = null
                    commitGreenAutoMapParam(GREEN_AUTO_MAP_POINTS_SLIDER.key, Number(event.currentTarget.value), true)
                  }}
                  onBlur={() => commitActiveGreenAutoMapSlider()}
                />
                <GreenAutoMapPointsValue>
                  {formatGreenAutoMapValue(
                    GREEN_AUTO_MAP_POINTS_SLIDER,
                    greenAutoMapDraftParams[GREEN_AUTO_MAP_POINTS_SLIDER.key],
                  )}
                </GreenAutoMapPointsValue>
              </SliderBackground>
            </GreenAutoMapToolbarSlider>
            {(() => {
              const loosenessKey = GREEN_AUTO_MAP_LOOSENESS_SLIDER.key
              const loosenessValue = greenAutoMapDraftParams[loosenessKey] as number
              const loosenessDisabled =
                greenAutoMapBusy ||
                greenAutoMapDraftParams.lPresetIndex >= 0 ||
                greenAutoMapDraftParams.abPresetIndex >= 0
              const adjustLooseness = (delta: number) => {
                const next = Math.min(
                  GREEN_AUTO_MAP_LOOSENESS_SLIDER.max,
                  Math.max(GREEN_AUTO_MAP_LOOSENESS_SLIDER.min, loosenessValue + delta),
                )
                if (next === loosenessValue) {
                  return
                }
                updateGreenAutoMapDraftParam(loosenessKey, next)
                commitGreenAutoMapParam(loosenessKey, next, true)
              }
              return (
                <GreenAutoMapToolbarStepper>
                  <GreenAutoMapToolbarSliderLabel>Looseness</GreenAutoMapToolbarSliderLabel>
                  <GreenAutoMapToolbarStepperButton
                    type="button"
                    onClick={() => adjustLooseness(-1)}
                    disabled={loosenessDisabled || loosenessValue <= GREEN_AUTO_MAP_LOOSENESS_SLIDER.min}
                    aria-label="Decrease looseness"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path
                        // eslint-disable-next-line max-len
                        d="M11.0835 6.41699C5.79394 6.41699 7.83473 6.41699 2.91683 6.41699C2.76212 6.41699 2.61375 6.47845 2.50435 6.58785C2.39495 6.69724 2.3335 6.84562 2.3335 7.00033C2.3335 7.15503 2.39495 7.30341 2.50435 7.4128C2.61375 7.5222 2.76212 7.58366 2.91683 7.58366C8.20638 7.58366 6.16559 7.58366 11.0835 7.58366C11.2382 7.58366 11.3866 7.5222 11.496 7.4128C11.6054 7.30341 11.6668 7.15503 11.6668 7.00033C11.6668 6.84562 11.6054 6.69724 11.496 6.58785C11.3866 6.47845 11.2382 6.41699 11.0835 6.41699Z"
                        fill="white"
                      />
                    </svg>
                  </GreenAutoMapToolbarStepperButton>
                  <GreenAutoMapToolbarStepperValue>
                    {formatGreenAutoMapValue(GREEN_AUTO_MAP_LOOSENESS_SLIDER, loosenessValue)}
                  </GreenAutoMapToolbarStepperValue>
                  <GreenAutoMapToolbarStepperButton
                    type="button"
                    onClick={() => adjustLooseness(1)}
                    disabled={loosenessDisabled || loosenessValue >= GREEN_AUTO_MAP_LOOSENESS_SLIDER.max}
                    aria-label="Increase looseness"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path
                        // eslint-disable-next-line max-len
                        d="M11.0835 6.41634H7.58349V2.91634C7.58349 2.76163 7.52204 2.61326 7.41264 2.50386C7.30324 2.39447 7.15487 2.33301 7.00016 2.33301C6.84545 2.33301 6.69708 2.39447 6.58768 2.50386C6.47829 2.61326 6.41683 2.76163 6.41683 2.91634V6.41634H2.91683C2.76212 6.41634 2.61375 6.4778 2.50435 6.58719C2.39495 6.69659 2.3335 6.84496 2.3335 6.99967C2.3335 7.15438 2.39495 7.30276 2.50435 7.41215C2.61375 7.52155 2.76212 7.58301 2.91683 7.58301H6.41683V11.083C6.41683 11.2377 6.47829 11.3861 6.58768 11.4955C6.69708 11.6049 6.84545 11.6663 7.00016 11.6663C7.15487 11.6663 7.30324 11.6049 7.41264 11.4955C7.52204 11.3861 7.58349 11.2377 7.58349 11.083V7.58301H11.0835C11.2382 7.58301 11.3866 7.52155 11.496 7.41215C11.6054 7.30276 11.6668 7.15438 11.6668 6.99967C11.6668 6.84496 11.6054 6.69659 11.496 6.58719C11.3866 6.4778 11.2382 6.41634 11.0835 6.41634Z"
                        fill="white"
                      />
                    </svg>
                  </GreenAutoMapToolbarStepperButton>
                </GreenAutoMapToolbarStepper>
              )
            })()}
            {showAdvancedPanel && (
              <GreenAutoMapToolbarToggle
                type="button"
                $active={greenAutoMapAdvancedOpen}
                onClick={() => setGreenAutoMapAdvancedOpen((prev) => !prev)}
              >
                <AdjustmentsIcon />
              </GreenAutoMapToolbarToggle>
            )}
            <IconDivider />
            <GreenAutoMapToolbarActions>
              <GreenAutoMapSecondaryButton type="button" onClick={handleGreenAutoMapRemap} disabled={greenAutoMapBusy}>
                <RemapIcon /> {greenAutoMapBusy ? 'Remapping...' : 'Remap'}
              </GreenAutoMapSecondaryButton>
              <GreenAutoMapSecondaryButton
                type="button"
                onClick={onCloseGreenAutoMapControls}
                disabled={greenAutoMapBusy}
              >
                <HazardToolIcon width={18} height={18} /> Manual
              </GreenAutoMapSecondaryButton>
              <GreenAutoMapPrimaryButton
                type="button"
                onClick={handleCloseGreenAutoMapSession}
                disabled={greenAutoMapBusy || !greenAutoMapPreviewReady}
              >
                <CheckCircleIcon />
                Looks good
              </GreenAutoMapPrimaryButton>
            </GreenAutoMapToolbarActions>
            {guidedGreenAutoMapActive && guidedGreenAutoMapProgressLabel && (
              <>
                <IconDivider />
                <GreenAutoMapToolbarStatus>{guidedGreenAutoMapProgressLabel}</GreenAutoMapToolbarStatus>
              </>
            )}
          </GreenAutoMapToolbar>
        )}

        {showGuidedManualToolbar ? (
          <GreenAutoMapToolbar>
            <GreenAutoMapToolbarActions>
              {showAutoMapAction && (
                <GreenToolSecondaryButton
                  type="button"
                  onClick={() => onAutoMapGreen(greenAutoMapDraftParams)}
                  disabled={greenAutoMapBusy}
                >
                  <MagicWandIcon /> Auto map
                </GreenToolSecondaryButton>
              )}
              {showFinishAction && (
                <GreenToolSecondaryButton
                  type="button"
                  onClick={onFinishGreenPolygon}
                  disabled={activeHolePolygonPointsCount < 3 || greenAutoMapBusy}
                >
                  Finish Polygon
                </GreenToolSecondaryButton>
              )}
              {showClearAction && (
                <GreenToolSecondaryButton type="button" onClick={onClearGreenPolygon} disabled={greenAutoMapBusy}>
                  <ClearIcon /> Clear
                </GreenToolSecondaryButton>
              )}
              <GreenAutoMapPrimaryButton
                type="button"
                onClick={onConfirmManualGuidedGreen}
                disabled={!greenPolygonFinished}
              >
                <CheckCircleIcon />
                Looks good
              </GreenAutoMapPrimaryButton>
            </GreenAutoMapToolbarActions>
            {guidedGreenAutoMapProgressLabel && (
              <>
                <IconDivider />
                <GreenAutoMapToolbarStatus>{guidedGreenAutoMapProgressLabel}</GreenAutoMapToolbarStatus>
              </>
            )}
          </GreenAutoMapToolbar>
        ) : (
          showGreenToolActions && (
            <GreenToolActions>
              {showAutoMapAction && (
                <GreenToolSecondaryButton
                  type="button"
                  onClick={() => onAutoMapGreen(greenAutoMapDraftParams)}
                  disabled={greenAutoMapBusy}
                >
                  <MagicWandIcon /> Auto map
                </GreenToolSecondaryButton>
              )}
              {showFinishAction && (
                <GreenToolSecondaryButton
                  type="button"
                  onClick={onFinishGreenPolygon}
                  disabled={activeHolePolygonPointsCount < 3 || greenAutoMapBusy}
                >
                  Finish Polygon
                </GreenToolSecondaryButton>
              )}
              {showClearAction && (
                <GreenToolSecondaryButton type="button" onClick={onClearGreenPolygon} disabled={greenAutoMapBusy}>
                  <ClearIcon /> Clear
                </GreenToolSecondaryButton>
              )}
            </GreenToolActions>
          )
        )}
      </GreenToolStack>
    </>
  )
}
